import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { lazy, memo, Suspense, useCallback, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import Loading from '../../components/layouts/Loading';
import { AuthContext } from '../../contexts/AuthContext';

const AlertMessage = lazy(() =>
  import('../../components/layouts/AlertMessage')
);
const Copyright = lazy(() => import('../../components/layouts/Copyright'));

const RegisterForm = () => {
  const { t } = useTranslation();
  const { registerUser } = useContext(AuthContext);
  let navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [registerForm, setRegisterForm] = useState({
    firstName: '',
    lastName: '',
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    address: '',
  });
  const [alert, setAlert] = useState(null);
  const {
    firstName,
    lastName,
    username,
    email,
    password,
    confirmPassword,
    phone,
    // address,
  } = registerForm;

  const onChangeRegisterForm = useCallback(
    (event) => {
      setRegisterForm({
        ...registerForm,
        [event.target.name]: event.target.value,
      });
    },
    [registerForm]
  );

  const handleClickShowPassword = useCallback(() => {
    setShowPassword(!showPassword);
  }, [showPassword]);

  const handleMouseDownPassword = useCallback((event) => {
    event.preventDefault();
  }, []);

  const onSubmit = useCallback(
    async (event) => {
      event.preventDefault();
      setAlert(null);
      if (!username) {
        setAlert({
          type: 'error',
          message: 'username is required',
        });
        return;
      }
      if (!lastName) {
        setAlert({
          type: 'error',
          message: 'lastName is required',
        });
        return;
      }
      if (!firstName) {
        setAlert({
          type: 'error',
          message: 'firstName is required',
        });
        return;
      }
      if (!email) {
        setAlert({
          type: 'error',
          message: 'email is required',
        });
        return;
      }
      if (!password) {
        setAlert({
          type: 'error',
          message: 'password is required',
        });
        return;
      }
      if (!confirmPassword) {
        setAlert({
          type: 'error',
          message: 'confirmPassword is required',
        });
        return;
      }
      if (!phone) {
        setAlert({
          type: 'error',
          message: 'phone is required',
        });
        return;
      }
      if (username.includes(' ')) {
        setAlert({
          type: 'warning',
          message: t('usernameNoSpace'),
        });
        return;
      }
      if (password.length < 6) {
        setAlert({
          type: 'warning',
          message: t('passwordCheck'),
        });
        return;
      }
      if (password !== confirmPassword) {
        setAlert({ type: 'warning', message: t('rePassword') });
        return;
      }
      try {
        const registerData = await registerUser({
          ...registerForm,
          fullName: firstName + ' ' + lastName,
        });
        if (!registerData.success) {
          setAlert({ type: 'error', message: registerData.message });
          setTimeout(() => setAlert(null), 30000);
        } else {
          navigate('/activate-account');
        }
      } catch (error) {
        console.log(error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      confirmPassword,
      firstName,
      navigate,
      lastName,
      password,
      registerForm,
      registerUser,
      username,
    ]
  );

  return (
    <>
      <Typography component="h1" variant="h5">
        {t(`register.title`)}
      </Typography>
      <Box component="form" onSubmit={onSubmit} sx={{ mt: 1 }}>
        <Suspense fallback={<Loading />}>
          <AlertMessage info={alert} />
        </Suspense>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t(`register.firstName`)}
              name="firstName"
              autoComplete="given-name"
              autoFocus
              onChange={onChangeRegisterForm}
              value={firstName}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t(`register.lastName`)}
              name="lastName"
              autoComplete="family-name"
              onChange={onChangeRegisterForm}
              value={lastName}
            />
          </Grid>
        </Grid>
        <TextField
          margin="normal"
          fullWidth
          label="Email"
          name="email"
          autoComplete="email"
          type="email"
          onChange={onChangeRegisterForm}
          value={email}
        />
        <TextField
          margin="normal"
          fullWidth
          label={t(`register.username`)}
          name="username"
          autoComplete="username"
          onChange={onChangeRegisterForm}
          value={username}
        />
        <TextField
          margin="normal"
          fullWidth
          name="password"
          label={t(`register.password`)}
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-password"
          onChange={onChangeRegisterForm}
          value={password}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          margin="normal"
          fullWidth
          name="confirmPassword"
          label={t(`register.rePassword`)}
          type={showPassword ? 'text' : 'password'}
          autoComplete="current-confirmPassword"
          onChange={onChangeRegisterForm}
          value={confirmPassword}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="toggle password visibility"
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  edge="end"
                >
                  {showPassword ? <Visibility /> : <VisibilityOff />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          margin="normal"
          fullWidth
          label={t(`register.phone`)}
          name="phone"
          autoComplete="phone"
          onChange={onChangeRegisterForm}
          value={phone}
        />
        <Button
          type="submit"
          fullWidth
          variant="contained"
          sx={{ mt: 3, mb: 2 }}
        >
          {t(`register.btn`)}
        </Button>
        <Grid container>
          <Grid item xs></Grid>
          <Grid item>
            <Link to="/login" variant="body2">
              {t(`register.login`)}
            </Link>
          </Grid>
        </Grid>
        <Suspense fallback={<Loading />}>
          <Copyright sx={{ mt: 5 }} />
        </Suspense>
      </Box>
    </>
  );
};

export default memo(RegisterForm);
